import type { WalletSourceToken } from './contracts'
import type { DecimalInputClassification } from '../domain/amounts'
import { ChainId } from '../domain/flow'
import { classifyDecimalInput, fromSmallestUnits } from '../domain/amounts'

// Token priority tiers - lower number = higher priority
export const TOKEN_PRIORITY_TIERS = {
  NATIVE: 1,
  USDT: 2,
  USDC: 3,
  STABLECOIN_OTHER: 4,
  OTHER: 5,
} as const

// Native token symbols by chain
const NATIVE_TOKEN_SYMBOLS: Record<number, string[]> = {
  1: ['ETH'], // Ethereum
  42161: ['ETH'], // Arbitrum
  8453: ['ETH'], // Base
  10: ['ETH'], // Optimism
  5426: ['SOL'], // Solana
}

const NATIVE_RESERVE_RAW: Readonly<Record<number, bigint>> = {
  [ChainId.ETHEREUM]: 5_000_000_000_000_000n,
  [ChainId.OPTIMISM]: 500_000_000_000_000n,
  [ChainId.ARBITRUM]: 200_000_000_000_000n,
  [ChainId.BASE]: 100_000_000_000_000n,
  [ChainId.SOLANA]: 10_000_000n,
}

const isNativeToken = (token: WalletSourceToken, chainId: number): boolean =>
  token.isNative || (NATIVE_TOKEN_SYMBOLS[chainId] ?? []).includes(token.symbol.toUpperCase())

const balanceStateRank = (token: WalletSourceToken): number => {
  if (token.hasBalance) return 0
  if (token.balanceStatus === 'unavailable') return 1
  return 2
}

// Known stablecoin symbols (excluding USDT/USDC which have special handling)
const STABLECOIN_SYMBOLS = new Set([
  'DAI',
  'BUSD',
  'TUSD',
  'USDP',
  'FRAX',
  'LUSD',
  'GUSD',
  'USDD',
  'USDX',
  'USDJ',
])

/**
 * Determine the priority tier for a token
 * Priority: native > USDT > USDC > other stablecoins > others
 */
export function getTokenPriorityTier(token: WalletSourceToken, chainId: number): number {
  const symbol = token.symbol.toUpperCase()

  const nativeSymbols = NATIVE_TOKEN_SYMBOLS[chainId] ?? []
  if (token.isNative || nativeSymbols.includes(symbol)) {
    return TOKEN_PRIORITY_TIERS.NATIVE
  }

  if (symbol === 'USDT') return TOKEN_PRIORITY_TIERS.USDT
  if (symbol === 'USDC') return TOKEN_PRIORITY_TIERS.USDC

  if (STABLECOIN_SYMBOLS.has(symbol)) {
    return TOKEN_PRIORITY_TIERS.STABLECOIN_OTHER
  }

  return TOKEN_PRIORITY_TIERS.OTHER
}

/**
 * Calculate USD value of token balance if price is available
 */
export function getTokenUsdValue(token: WalletSourceToken): number {
  if (!token.priceUsd || !token.balanceFormatted) return 0
  const balance = parseFloat(token.balanceFormatted)
  if (isNaN(balance) || balance <= 0) return 0
  return balance * token.priceUsd
}

/**
 * Sort tokens by priority:
 * 1. Priority tier (native > USDT > USDC > stablecoins > others)
 * 2. For funded tokens: USD value (descending), then raw balance
 * 3. Alphabetical by symbol as final tiebreaker
 */
export function sortTokensByPriority(
  tokens: WalletSourceToken[],
  chainId: number
): WalletSourceToken[] {
  return [...tokens].sort((a, b) => {
    const stateRankA = balanceStateRank(a)
    const stateRankB = balanceStateRank(b)
    if (stateRankA !== stateRankB) return stateRankA - stateRankB

    const tierA = getTokenPriorityTier(a, chainId)
    const tierB = getTokenPriorityTier(b, chainId)

    // First: priority tier
    if (tierA !== tierB) {
      return tierA - tierB
    }

    // Second: has balance vs no balance
    const hasBalanceA = a.hasBalance ? 1 : 0
    const hasBalanceB = b.hasBalance ? 1 : 0
    if (hasBalanceA !== hasBalanceB) {
      return hasBalanceB - hasBalanceA
    }

    // Third: for funded tokens, sort by USD value (descending)
    if (a.hasBalance && b.hasBalance) {
      const usdA = getTokenUsdValue(a)
      const usdB = getTokenUsdValue(b)

      if (usdA > 0 && usdB > 0 && Math.abs(usdA - usdB) > 0.01) {
        return usdB - usdA
      }

      // If no USD prices or similar value, sort by raw balance
      const balanceA = parseFloat(a.balanceFormatted)
      const balanceB = parseFloat(b.balanceFormatted)
      if (balanceA !== balanceB) {
        return balanceB - balanceA
      }
    }

    // Final: alphabetical by symbol
    return a.symbol.localeCompare(b.symbol)
  })
}

/**
 * Select the best default token from a list
 * Returns the highest priority token with balance, or the highest priority token if none have balance
 */
export function selectDefaultToken(
  tokens: WalletSourceToken[],
  chainId: number
): WalletSourceToken | undefined {
  if (tokens.length === 0) return undefined

  const sorted = sortTokensByPriority(tokens, chainId)

  // Try to find the highest priority token with balance
  const withBalance = sorted.find((t) => t.hasBalance)
  if (withBalance) return withBalance

  // If no tokens have balance, return the highest priority one
  return sorted[0]
}

// Smart amount presets by token type
export const SMART_AMOUNT_PRESETS = {
  // Stablecoins: conservative starter amount
  STABLECOIN: '25',

  // ETH-family chains: fast first route check
  ETH: '0.01',

  // Solana: fast first route check
  SOL: '0.1',

  // Generic fallback: small amount
  DEFAULT: '10',
} as const

const SMART_ROUTE_MINIMUM_USD: Record<number, number> = {
  [ChainId.ETHEREUM]: 20,
  [ChainId.SOLANA]: 5,
}

export function getChainMinimumUsd(chainId: number): number {
  return SMART_ROUTE_MINIMUM_USD[chainId] ?? 10
}

/**
 * Get smart amount preset for a token
 */
export function getSmartAmountPreset(token: WalletSourceToken, chainId: number): string {
  const symbol = token.symbol.toUpperCase()

  // Stablecoins
  if (symbol === 'USDT' || symbol === 'USDC' || symbol === 'DAI') {
    return SMART_AMOUNT_PRESETS.STABLECOIN
  }

  // Native tokens
  const nativeSymbols = NATIVE_TOKEN_SYMBOLS[chainId] ?? []
  if (token.isNative || nativeSymbols.includes(symbol)) {
    if (symbol === 'ETH') return SMART_AMOUNT_PRESETS.ETH
    if (symbol === 'SOL') return SMART_AMOUNT_PRESETS.SOL
  }

  return SMART_AMOUNT_PRESETS.DEFAULT
}

/**
 * Calculate spendable balance for a token
 * For native tokens, subtracts a fee reserve
 */
export function calculateSpendableBalance(token: WalletSourceToken, chainId: number): number {
  return Number(
    fromSmallestUnits(calculateSpendableBalanceRaw(token, chainId).toString(), token.decimals)
  )
}

export function calculateSpendableBalanceRaw(token: WalletSourceToken, chainId: number): bigint {
  if (token.balanceStatus === 'unavailable' || !/^(0|[1-9]\d*)$/.test(token.balanceRaw)) {
    return 0n
  }
  const balance = BigInt(token.balanceRaw)
  const reserve = isNativeToken(token, chainId) ? (NATIVE_RESERVE_RAW[chainId] ?? 0n) : 0n
  return balance > reserve ? balance - reserve : 0n
}

export interface SourceAmountAssessment {
  quoteEligible: boolean
  validationError: string | null
}

const NON_VALID_AMOUNT_ASSESSMENTS = {
  empty: { quoteEligible: false, validationError: null },
  incomplete: { quoteEligible: false, validationError: null },
  excess_precision: {
    quoteEligible: false,
    validationError: 'Amount has too many decimal places',
  },
  invalid: { quoteEligible: false, validationError: 'Enter a valid amount' },
} as const satisfies Record<
  Exclude<DecimalInputClassification['kind'], 'valid'>,
  SourceAmountAssessment
>

const hasKnownPositiveBalanceBelowReserve = (token: WalletSourceToken, chainId: number): boolean =>
  token.hasBalance &&
  token.balanceStatus !== 'unavailable' &&
  /^(0|[1-9]\d*)$/.test(token.balanceRaw) &&
  BigInt(token.balanceRaw) > 0n &&
  isNativeToken(token, chainId) &&
  calculateSpendableBalanceRaw(token, chainId) === 0n

const zeroSpendableValidationError = (token: WalletSourceToken, chainId: number): string => {
  const priceUsd = token.priceUsd
  if (typeof priceUsd === 'number' && Number.isFinite(priceUsd) && priceUsd > 0) {
    const balance = Number(fromSmallestUnits(token.balanceRaw, token.decimals))
    const balanceUsd = balance * priceUsd
    const minUsd = getChainMinimumUsd(chainId)
    if (Number.isFinite(balanceUsd) && balanceUsd < minUsd - 0.01) {
      return `Minimum bridge amount is $${String(minUsd)}. Your balance: ~$${balanceUsd.toFixed(2)}`
    }
  }

  return 'Not enough balance available after network fees.'
}

const minimumValidationError = (
  amount: string,
  priceUsd: number | null | undefined,
  chainId: number
): string | null => {
  if (typeof priceUsd !== 'number' || !Number.isFinite(priceUsd) || priceUsd <= 0) return null
  const usdValue = Number(amount) * priceUsd
  const minUsd = getChainMinimumUsd(chainId)
  return usdValue < minUsd - 0.01
    ? `Minimum bridge amount is $${String(minUsd)}. Current value: ~$${usdValue.toFixed(2)}`
    : null
}

export function assessSourceAmount(
  amount: string,
  token: WalletSourceToken,
  chainId: number
): SourceAmountAssessment {
  if (hasKnownPositiveBalanceBelowReserve(token, chainId)) {
    return {
      quoteEligible: false,
      validationError: zeroSpendableValidationError(token, chainId),
    }
  }

  const classified = classifyDecimalInput(amount, token.decimals)
  if (classified.kind !== 'valid') {
    return NON_VALID_AMOUNT_ASSESSMENTS[classified.kind]
  }
  if (classified.units <= 0n) return { quoteEligible: false, validationError: null }

  const minimumError = minimumValidationError(amount, token.priceUsd, chainId)
  if (minimumError) return { quoteEligible: false, validationError: minimumError }

  if (classified.units > calculateSpendableBalanceRaw(token, chainId)) {
    return {
      quoteEligible: false,
      validationError: isNativeToken(token, chainId)
        ? 'Amount exceeds spendable balance after network fee reserve'
        : 'Insufficient balance',
    }
  }

  return { quoteEligible: true, validationError: null }
}

/**
 * Get smart default amount that respects user balance
 * Returns the preset amount, capped at spendable balance
 */
export function getSmartDefaultAmount(token: WalletSourceToken, chainId: number): string {
  const preset = getSmartAmountPreset(token, chainId)
  const presetValue = parseFloat(preset)

  const spendable = calculateSpendableBalance(token, chainId)
  if (spendable <= 0) return ''

  const minUsd = getChainMinimumUsd(chainId)
  const priceUsd = token.priceUsd
  const hasPrice = typeof priceUsd === 'number' && priceUsd > 0
  // Round up 1% so decimal truncation stays above the provider's USD minimum.
  const minimumAmountByUsd = hasPrice ? (minUsd / priceUsd) * 1.01 : 0
  const targetAmount = Math.max(presetValue, minimumAmountByUsd)

  // If preset exceeds spendable balance, use a percentage of spendable
  if (targetAmount > spendable) {
    // Use 90% of balance for native tokens (leave some buffer)
    // Use 100% for non-native
    const symbol = token.symbol.toUpperCase()
    const nativeSymbols = NATIVE_TOKEN_SYMBOLS[chainId] ?? []
    const isNative = token.isNative || nativeSymbols.includes(symbol)

    const usePercentage = isNative ? 0.9 : 1.0
    const adjustedAmount = spendable * usePercentage

    // Format to reasonable decimals
    if (adjustedAmount >= 1) {
      return adjustedAmount.toFixed(4)
    } else if (adjustedAmount >= 0.001) {
      return adjustedAmount.toFixed(6)
    } else {
      return adjustedAmount.toFixed(8)
    }
  }

  return formatSmartAmount(targetAmount, token.decimals)
}

function formatSmartAmount(amount: number, decimals: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return ''

  if (amount >= 1000) return amount.toFixed(0)
  if (amount >= 1) return trimTrailingZeros(amount.toFixed(Math.min(decimals, 4)))
  if (amount >= 0.01) return trimTrailingZeros(amount.toFixed(Math.min(decimals, 4)))
  if (amount >= 0.001) return trimTrailingZeros(amount.toFixed(Math.min(decimals, 6)))
  return trimTrailingZeros(amount.toFixed(Math.min(decimals, 8)))
}

function trimTrailingZeros(value: string): string {
  return value.replace(/\.0+$/, '').replace(/(\.\d*?[1-9])0+$/, '$1')
}

export function formatTokenBalance(balance: string): string {
  const num = parseFloat(balance)
  if (num === 0) return '0'
  if (num < 0.0001) return '<0.0001'
  if (num < 1) return num.toFixed(6)
  if (num < 1000) return num.toFixed(4)
  if (num < 1000000) return `${(num / 1000).toFixed(2)}K`
  return `${(num / 1000000).toFixed(2)}M`
}
